import { useCallback, useRef } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FileNode } from '@ncoder/ui';
import { useCopyToClipboard } from '@/hooks/multi-agent/useCopyToClipboard';
import { extractComponentName, getFiletypeLabel } from '@/constants/metadata/bundleTypes';

interface BundleNavStripProps {
  /** The currently active file in the editor. */
  activeFile: FileNode;
  /** The other files in the same bundle (excludes the active file). */
  siblings: FileNode[];
  /** Called when the user selects a sibling file. */
  onSelectFile: (file: FileNode) => void;
}

/**
 * BundleNavStrip — renders a compact context bar between the tab strip and the editor.
 *
 * Layout:  [ComponentName]  [copy]  |  [HTML]  [JS]  [CSS]  [META]
 *
 * The active sibling is highlighted. Clicking any sibling opens it via onSelectFile.
 * Supports keyboard navigation (ArrowLeft/Right between file-type buttons).
 */
export function BundleNavStrip({ activeFile, siblings, onSelectFile }: BundleNavStripProps) {
  const { copied, copy } = useCopyToClipboard();
  const containerRef = useRef<HTMLDivElement>(null);

  const componentName = extractComponentName(activeFile.name);

  const handleCopy = useCallback(() => {
    copy(componentName);
  }, [copy, componentName]);

  // Build sorted file list: active first, then siblings in stable order
  const allFiles = [activeFile, ...siblings];

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>('[data-file-btn]');
    if (!buttons) return;
    let next = -1;
    if (e.key === 'ArrowRight') next = Math.min(index + 1, buttons.length - 1);
    if (e.key === 'ArrowLeft') next = Math.max(index - 1, 0);
    if (next >= 0) {
      e.preventDefault();
      buttons[next].focus();
    }
  }, []);

  return (
    <div
      className="flex items-center gap-0 px-2 h-7 text-xs bg-background select-none overflow-x-auto"
      aria-label="Bundle file navigation"
    >
      {/* Component name + copy button */}
      <span className="flex items-center gap-1 shrink-0 text-muted-foreground font-medium pr-2 mr-2 border-r border-border">
        <span className="truncate max-w-[160px]" title={componentName}>
          {componentName}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          title="Copy component name"
          className={cn(
            'h-4 w-4 flex items-center justify-center rounded transition-colors',
            'hover:bg-muted hover:text-foreground text-muted-foreground/60',
            copied && 'text-green-500'
          )}
          aria-label="Copy component name"
        >
          {copied ? (
            <Check className="h-3 w-3" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </button>
      </span>

      {/* Sibling file-type buttons */}
      <div ref={containerRef} className="flex items-center gap-0.5" role="tablist" aria-label="Bundle files">
        {allFiles.map((file, index) => {
          const isActive = file.id === activeFile.id;
          const label = getFiletypeLabel(file.name);
          return (
            <button
              key={file.id}
              type="button"
              role="tab"
              data-file-btn
              aria-selected={isActive}
              title={file.name}
              onClick={() => !isActive && onSelectFile(file)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={cn(
                'px-2 h-5 rounded text-xs font-medium transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                isActive
                  ? 'bg-accent text-accent-foreground cursor-default'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer'
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
